import { DOCUMENT } from '@angular/common';
import { Injectable, inject, signal, computed } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { ApplicationLanguages } from 'src/app/enums/lang';

@Injectable({
  providedIn: 'root',
})
export class ChangeLangService {
  private readonly document = inject<Document>(DOCUMENT);
  private readonly translateService = inject(TranslateService);
  private readonly router = inject(Router);

  readonly ApplicationLanguages = ApplicationLanguages;

  readonly currentLanguage = signal<string>(ApplicationLanguages.Ar);

  readonly isRTL = computed(
    () => this.currentLanguage() === ApplicationLanguages.Ar
  );

  readonly supportedLanguages = Object.values(ApplicationLanguages);

  constructor() {
    this.initializeLanguage();
  }

  private initializeLanguage(): void {
    const initialLang =
      this.getStoredLanguage() ||
      this.translateService.currentLang ||
      ApplicationLanguages.Ar;

    this.setInitialAppLanguage(initialLang);
  }

  private getStoredLanguage(): string | null {
    try {
      return localStorage.getItem('lang');
    } catch {
      return null;
    }
  }

  private storeLanguage(lang: string): void {
    try {
      localStorage.setItem('lang', lang);
    } catch {
      console.warn('Failed to store language preference');
    }
  }

  setInitialAppLanguage(lang: string): void {
    if (!this.isValidLanguage(lang)) {
      lang = ApplicationLanguages.Ar;
    }

    this.currentLanguage.set(lang);
    this.setDefaultLanguage(lang);
    this.updateDocumentDirection(lang);
    this.storeLanguage(lang);
  }

  private updateDocumentDirection(lang: string): void {
    const isRTL = lang === ApplicationLanguages.Ar;
    const htmlElement = this.document.documentElement;
    const bodyElement = this.document.body;

    // Update HTML attributes
    htmlElement.dir = isRTL ? 'rtl' : 'ltr';
    htmlElement.lang = lang;
    htmlElement.setAttribute('dir', isRTL ? 'rtl' : 'ltr');

    // Update body classes
    bodyElement?.classList.remove('rtl', 'ltr');
    bodyElement?.classList.add(isRTL ? 'rtl' : 'ltr');
  }

  private setDefaultLanguage(lang: string): void {
    this.translateService.setDefaultLang(lang);
    this.translateService.use(lang);
  }

  changeLanguage(lang: string): void {
    if (!this.isValidLanguage(lang)) {
      lang = ApplicationLanguages.Ar;
    }

    if (this.currentLanguage() === lang) {
      return;
    }

    this.currentLanguage.set(lang);
    this.storeLanguage(lang);
    this.navigateWithNewLanguage(lang);
  }

  private navigateWithNewLanguage(lang: string): void {
    const urlSegments = this.router.url.split('/');

    if (urlSegments.length > 1) {
      urlSegments[1] = lang;
      const newUrl = urlSegments.join('/');
      window.location.href = newUrl;
    }
  }

  private isValidLanguage(lang: string): boolean {
    return this.supportedLanguages.includes(lang as ApplicationLanguages);
  }
}
